import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link, NavLink } from "react-router-dom";
import { PanelLeft, PanelRight, ChevronLeft, LayoutList, Share2, SlidersHorizontal, Sparkles, Layers } from "lucide-react";
import { useStudio, useProjectData } from "@/store/StudioContext";
import { useAuth } from "@/store/AuthContext";
import { useWindowWidth } from "@/hooks/useWindowWidth";
import { ErrorState, DemoBadge } from "@/components/common/States";
import { Page } from "@/components/layout/PageHeader";
import StoryboardView from "@/components/project/StoryboardView";
import CanvasView from "@/components/project/CanvasView";
import EditorView from "@/components/project/EditorView";
import CharactersPage from "@/pages/CharactersPage";
import AssistantPanel from "@/components/project/AssistantPanel";
import LibraryPanel from "@/components/project/LibraryPanel";
import { STANDARD_FORMATS } from "@/domain/formats";
import { t } from "@/i18n";

const VIEWS = [
  { id: "storyboard", icon: LayoutList },
  { id: "canvas", icon: Share2 },
  { id: "editor", icon: SlidersHorizontal },
  { id: "resources", icon: Layers },
];

const PANEL_W = 288;
const PROJECT_FORMATS = [...STANDARD_FORMATS];

const ProjectWorkspace = () => {
  const { projectId, view } = useParams();
  const navigate = useNavigate();
  const { state, dispatch } = useStudio();
  const { isOwner } = useAuth();
  const data = useProjectData(projectId);
  const width = useWindowWidth();
  const isMobile = width < 768;
  const isWide = width >= 1400;

  const [libraryOpen, setLibraryOpen] = useState(() => {
    try {
      const savedProjectPref = localStorage.getItem(`veyra_library_open_${projectId}`);
      if (savedProjectPref !== null) return JSON.parse(savedProjectPref);

      const savedGlobalPref = localStorage.getItem("veyra_library_open");
      if (savedGlobalPref !== null) return JSON.parse(savedGlobalPref);

      // Un proyecto nuevo (sin escenas) abre con la biblioteca secundaria plegada
      const isNewProject = !data?.scenes || data.scenes.length === 0;
      if (isNewProject) return false;

      // Para proyectos existentes del Owner con escenas: conservar preferencia previa
      return window.innerWidth >= 1400;
    } catch {
      return false;
    }
  });

  // Preferencias visuales persistentes del VEYRA Creative Director
  const [assistantWidth, setAssistantWidthState] = useState(() => {
    try {
      const saved = localStorage.getItem("veyra_creative_director_width");
      return saved ? Math.max(320, Math.min(800, Number(saved))) : 384;
    } catch {
      return 384;
    }
  });

  // Al entrar al proyecto desde Inicio/Proyectos, abrir siempre en vista normal (modo lateral).
  // No restaurar automáticamente modo ampliado o pantalla completa en esa entrada.
  const [assistantMode, setAssistantModeState] = useState("normal");

  const [assistantOpen, setAssistantOpenState] = useState(() => {
    // Si el usuario no tiene permisos de Creative Director (invitado), mantener cerrado
    if (!isOwner) return false;
    try {
      if (state?.assistantPanel?.open === false) return false;
      const isSmall = typeof window !== "undefined" && window.innerWidth < 768;
      if (isSmall) return false;
      const saved = localStorage.getItem("veyra_creative_director_open");
      if (saved !== null) return JSON.parse(saved);
      return window.innerWidth >= 1200;
    } catch {
      return false;
    }
  });

  // Rastrear el proyecto activo para restablecer modo normal al cambiar o entrar de nuevo a un proyecto.
  // Dentro del mismo proyecto, navegar entre vistas (storyboard, canvas, editor) conserva
  // el modo y estado abierto/cerrado establecido manualmente por el usuario.
  const activeProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (activeProjectIdRef.current !== projectId) {
      activeProjectIdRef.current = projectId;
      setAssistantModeState("normal");
      if (isOwner) {
        const isSmall = typeof window !== "undefined" && window.innerWidth < 768;
        setAssistantOpenState(!isSmall);
      } else {
        setAssistantOpenState(false);
      }
    }
  }, [projectId, isOwner]);

  const [isResizing, setIsResizing] = useState(false);
  const [editingName, setEditingName] = useState(false);

  // Setters sincronizados con localStorage
  const setAssistantWidth = useCallback((w) => {
    setAssistantWidthState(w);
    try {
      localStorage.setItem("veyra_creative_director_width", String(w));
    } catch {
      // ignore
    }
  }, []);

  const setAssistantMode = useCallback((m) => {
    setAssistantModeState(m);
    try {
      localStorage.setItem("veyra_creative_director_mode", m);
    } catch {
      // ignore
    }
  }, []);

  const setAssistantOpen = useCallback((valOrFn) => {
    setAssistantOpenState((prev) => {
      const next = typeof valOrFn === "function" ? valOrFn(prev) : valOrFn;
      try {
        localStorage.setItem("veyra_creative_director_open", JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  // Control de arrastre para redimensionar el panel desde el borde izquierdo
  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      setIsResizing(true);
      const startX = e.clientX;
      const startW = assistantWidth;
      const centerMin = 400;
      const libW = libraryOpen ? (isWide ? 288 : 0) : 0;
      const maxW = Math.max(340, window.innerWidth - libW - centerMin);

      const onPointerMove = (moveEvent) => {
        const deltaX = startX - moveEvent.clientX;
        const targetW = Math.max(320, Math.min(maxW, startW + deltaX));
        setAssistantWidth(targetW);
        if (assistantMode !== "normal") {
          setAssistantMode("normal");
        }
      };

      const onPointerUp = () => {
        setIsResizing(false);
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [assistantWidth, assistantMode, isWide, libraryOpen, setAssistantMode, setAssistantWidth]
  );

  // Si la pantalla se estrecha por debajo de 1200px y ambos paneles están abiertos en modo escritorio
  useEffect(() => {
    if (!isWide && libraryOpen && assistantOpen && assistantMode !== "fullscreen") {
      setLibraryOpen(false);
    }
  }, [isWide]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data.project) {
    return (
      <Page testId="project-not-found">
        <ErrorState
          title={t.project.notFound}
          body={t.project.notFoundBody}
          action={
            <Link to="/projects" className="v-btn-primary v-btn-sm" data-testid="project-back">
              {t.project.backToProjects}
            </Link>
          }
        />
      </Page>
    );
  }
  if (!VIEWS.some((v) => v.id === view)) {
    navigate(`/projects/${projectId}/storyboard`, { replace: true });
    return null;
  }

  const { project, scenes, selection } = data;
  const selectedScene = scenes.find((s) => s.id === selection.sceneId);

  const toggleLibrary = () => {
    if (!isWide && !libraryOpen) setAssistantOpen(false);
    setLibraryOpen((o) => {
      const next = !o;
      try {
        localStorage.setItem(`veyra_library_open_${projectId}`, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const toggleAssistant = () => {
    if (!isOwner) return;
    if (!isWide && !assistantOpen) setLibraryOpen(false);
    setAssistantOpen((o) => !o);
  };

  const libraryClass = !libraryOpen
    ? "hidden"
    : isMobile
      ? "relative h-[38vh] border-b border-[#303030] w-full"
      : isWide
        ? "relative border-r border-[#303030]"
        : "absolute inset-y-0 left-0 z-20 border-r border-[#303030] shadow-[12px_0_32px_rgba(0,0,0,0.55)]";

  const isFullscreen = assistantOpen && assistantMode === "fullscreen";
  const isExpanded = assistantOpen && assistantMode === "expanded";

  // Estilos de ancho del asistente según modo
  const assistantContainerStyle = !assistantOpen
    ? undefined
    : isMobile
      ? undefined
      : isFullscreen
        ? { width: "100%" }
        : isExpanded
          ? { width: "50%" }
          : { width: `${assistantWidth}px` };

  const assistantClass = !assistantOpen
    ? "hidden"
    : isMobile
      ? "relative h-[55vh] border-t border-[#303030] w-full"
      : isFullscreen
        ? "relative w-full h-full"
        : isWide
          ? "relative border-l border-[#303030]"
          : "absolute inset-y-0 right-0 z-20 border-l border-[#303030] shadow-[-12px_0_32px_rgba(0,0,0,0.55)]";

  const overlayWidth = isMobile ? undefined : { width: PANEL_W };
  const showBackdrop = !isWide && !isMobile && (libraryOpen || assistantOpen) && !isFullscreen;

  return (
    <div data-testid="project-workspace" className="flex h-full min-h-0 flex-1 flex-col">
      {/* Escudo para captura de puntero durante redimensionamiento */}
      {isResizing && (
        <div className="fixed inset-0 z-50 cursor-col-resize select-none pointer-events-auto" />
      )}

      {/* Header principal de VEYRA */}
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[#303030] bg-[#141414] px-3 md:px-4">
        <Link
          to="/projects"
          data-testid="project-back-link"
          title={t.project.backToProjects}
          className="rounded-md p-1.5 text-[#a3a3a3] hover:bg-[#222] hover:text-[#f4f1ec]"
          aria-label={t.project.backToProjects}
        >
          <ChevronLeft size={18} />
        </Link>
        <div className="flex min-w-0 items-center gap-2">
          {editingName ? (
            <input
              autoFocus
              data-testid="project-name-input"
              className="v-input h-8 w-56"
              defaultValue={project.name}
              onBlur={(e) => {
                dispatch({
                  type: "PROJECT_UPDATE",
                  payload: { id: project.id, patch: { name: e.target.value.trim() || t.common.untitled } },
                });
                setEditingName(false);
              }}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            />
          ) : (
            <button
              data-testid="project-name"
              onClick={() => setEditingName(true)}
              className="truncate font-display text-base font-semibold hover:text-[#f5b544]"
              title={t.common.edit}
            >
              {project.name}
            </button>
          )}
          {project.isDemo && <DemoBadge />}
          <select
            data-testid="project-format"
            className="hidden h-7 rounded-md border border-[#303030] bg-transparent px-1.5 text-xs text-[#a3a3a3] md:block"
            value={project.format}
            onChange={(e) =>
              dispatch({ type: "PROJECT_UPDATE", payload: { id: project.id, patch: { format: e.target.value } } })
            }
          >
            {PROJECT_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f === "auto" ? t.tools.formatAuto : f}
              </option>
            ))}
          </select>
        </div>

        <nav
          className="mx-auto flex items-center rounded-lg border border-[#303030] bg-[#101010] p-0.5"
          data-testid="project-view-tabs"
        >
          {VIEWS.map(({ id, icon: Icon }) => (
            <NavLink
              key={id}
              to={`/projects/${projectId}/${id}`}
              data-testid={`project-tab-${id}`}
              title={t.project.views[id]}
              className={({ isActive }) =>
                `flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors ${
                  isActive ? "bg-[#f5b544] text-[#1a1206]" : "text-[#a3a3a3] hover:text-[#f4f1ec]"
                }`
              }
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{t.project.views[id]}</span>
            </NavLink>
          ))}
        </nav>

        <div
          className="hidden items-center gap-1 text-xs text-[#a3a3a3] lg:flex"
          data-testid="project-selection-indicator"
        >
          <span className="v-eyebrow">{t.project.selectedScene}:</span>
          <span className="max-w-[180px] truncate text-[#d8d4cd]">
            {selectedScene ? selectedScene.title || t.common.untitled : t.project.noSelection}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {!isFullscreen && (
            <button
              data-testid="toggle-library"
              onClick={toggleLibrary}
              className={`rounded-md p-1.5 hover:bg-[#222] ${libraryOpen ? "text-[#f5b544]" : "text-[#a3a3a3]"}`}
              aria-label={libraryOpen ? t.project.hideLibrary : t.project.showLibrary}
              title={libraryOpen ? t.project.hideLibrary : t.project.showLibrary}
            >
              <PanelLeft size={18} />
            </button>
          )}

          {isOwner && (
            <button
              data-testid="toggle-assistant"
              onClick={toggleAssistant}
              className={`rounded-md p-1.5 transition-colors border ${
                assistantOpen
                  ? "border-[#f5b544]/50 bg-[#221c10] text-[#f5b544]"
                  : "border-transparent text-[#a3a3a3] hover:bg-[#222] hover:text-[#f4f1ec]"
              }`}
              aria-label={assistantOpen ? t.project.hideAssistant : t.project.showAssistant}
              aria-pressed={assistantOpen}
              title={assistantOpen ? t.project.hideAssistant : t.project.showAssistant}
            >
              <PanelRight size={18} />
            </button>
          )}
        </div>
      </header>

      {/* Cuerpo principal del workspace */}
      <div className="relative flex min-h-0 flex-1 flex-col md:flex-row overflow-hidden">
        {showBackdrop && (
          <div
            data-testid="workspace-panel-backdrop"
            className="absolute inset-0 z-10 bg-black/40"
            onClick={() => {
              setLibraryOpen(false);
              setAssistantOpen(false);
            }}
          />
        )}

        {/* Panel lateral izquierdo: Biblioteca */}
        <aside
          data-testid="library-panel"
          className={`shrink-0 overflow-hidden bg-[#141414] transition-[width] ${
            isFullscreen ? "hidden" : libraryClass
          }`}
          style={libraryOpen ? overlayWidth : undefined}
        >
          <LibraryPanel data={data} />
        </aside>

        {/* Área central: Storyboard / Lienzo / Editor */}
        <section
          className={`relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${
            isFullscreen ? "hidden" : ""
          }`}
        >
          {view === "storyboard" && <StoryboardView data={data} />}
          {view === "canvas" && <CanvasView data={data} />}
          {view === "editor" && <EditorView data={data} />}
          {view === "resources" && (
            <div className="flex-1 overflow-y-auto">
              <CharactersPage embeddedProjectId={projectId} />
            </div>
          )}
        </section>

        {/* Pestaña docked lateral cuando el asistente está contraído en espacio reservado (solo Owner) */}
        {isOwner && !assistantOpen && !isFullscreen && (
          <aside
            data-testid="assistant-dock-rail"
            className="shrink-0 flex flex-col justify-end items-center border-l border-[#262626] bg-[#121212] px-1 py-3 z-20"
          >
            <button
              type="button"
              data-testid="assistant-dock-tab"
              onClick={() => setAssistantOpen(true)}
              title={t.project.showAssistant || t.assistant.title}
              aria-label={t.project.showAssistant || t.assistant.title}
              className="flex items-center gap-1.5 rounded border border-[#303030] bg-[#181818] p-2 text-xs font-medium text-[#d8d4cd] transition-all hover:bg-[#1f190e] hover:border-[#f5b544]/60 hover:text-[#f5b544] [writing-mode:vertical-rl] rotate-180"
            >
              <Sparkles size={13} className="text-[#f5b544] rotate-180" />
              <span className="font-semibold tracking-wide text-[11px]">Creative Director</span>
            </button>
          </aside>
        )}

        {/* Panel lateral derecho: VEYRA Creative Director (solo Owner) */}
        {isOwner && (
          <aside
            data-testid="assistant-panel"
            className={`shrink-0 bg-[#141414] transition-[width] ${assistantClass}`}
            style={assistantContainerStyle}
          >
            {/* Manija de arrastre (resize handle) para redimensionar el panel */}
            {assistantOpen && !isMobile && !isFullscreen && (
              <div
                data-testid="assistant-resize-handle"
                onPointerDown={startResize}
                className="group absolute -left-1.5 top-0 bottom-0 z-30 w-3 cursor-col-resize select-none flex items-center justify-center transition-colors"
                title="Arrastrar para redimensionar panel"
              >
                <div
                  className={`h-8 w-1 rounded-full transition-colors ${
                    isResizing ? "bg-[#f5b544]" : "bg-[#333] group-hover:bg-[#f5b544]/80"
                  }`}
                />
              </div>
            )}

            <AssistantPanel
              data={data}
              mode={assistantMode}
              onModeChange={setAssistantMode}
              width={assistantWidth}
              onWidthChange={setAssistantWidth}
              isOpen={assistantOpen}
              onClose={() => setAssistantOpen(false)}
              isMobile={isMobile}
              isWide={isWide}
              onNavigate={navigate}
            />
          </aside>
        )}
      </div>
    </div>
  );
};

export default ProjectWorkspace;
